// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  Building2, 
  Network, 
  Store, 
  Shield, 
  Zap, 
  CheckCircle2,
  ArrowRight,
  Globe,
  Lock,
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const Home = () => {
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    type: "",
    message: ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Thank you for your interest! We'll be in touch shortly.");
    setIsPartnerModalOpen(false);
    setIsContactModalOpen(false);
    setFormData({ name: "", company: "", email: "", phone: "", type: "", message: "" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="home-page">
      {/* Hero Section with Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950" />
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }} />
        </div>
        
        {/* Floating Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-50 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Wallet className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-white">360Wallet</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-sm text-gray-300 hover:text-white transition-colors">About</a>
              <a href="#partners" className="text-sm text-gray-300 hover:text-white transition-colors">Partners</a>
              <a href="#benefits" className="text-sm text-gray-300 hover:text-white transition-colors">Platform</a>
              <a href="#contact" className="text-sm text-gray-300 hover:text-white transition-colors">Contact</a>
            </div>
            
            <Button 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => setIsContactModalOpen(true)}
            >
              Get in Touch
            </Button>
          </div>
        </nav>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm text-blue-300">National Payment Architecture Utility</span>
          </div>
          
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
            Decoupled Payment Orchestration Engine
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mt-2">
              & Settlement Architecture
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            360Wallet is a payment architecture utility built around a decoupled, interoperable orchestration layer. It is designed to coordinate cardless ATM withdrawals and cross-bank deposits across participating banks, ATM operators and retail endpoints, without requiring changes to the core banking stack. Its modular architecture supports scalable participant onboarding, subject to the applicable connectivity, settlement, security and regulatory requirements.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 shadow-lg shadow-blue-600/30"
              onClick={() => setIsPartnerModalOpen(true)}
              data-testid="request-partnership-btn"
            >
              Request Partnership
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10 px-8"
              onClick={() => setIsContactModalOpen(true)}
              data-testid="contact-btn"
            >
              Contact Us
            </Button>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 rounded-full bg-white/60 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Value Statement Section */}
      <section id="about" className="py-24 bg-slate-950">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed">
            <span className="text-white font-medium">360Wallet</span> connects financial institutions and cash-out networks through a secure interoperability layer — enabling <span className="text-blue-400">cardless ATM withdrawals</span> and <span className="text-green-400">real-time cross-bank deposits</span> without additional physical infrastructure.
          </p>
        </div>
      </section>

      {/* Two-Way Interoperability Section — NEW */}
      <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-4">
              <Clock className="h-4 w-4 text-green-400" />
              <span className="text-sm text-green-300">Real-Time Clearing</span>
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Cash Flows Both Ways
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A single authorization platform for the full cash lifecycle — into and out of any participating bank
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Withdrawal card */}
            <Card className="bg-slate-900/60 border-blue-500/20 hover:border-blue-500/50 transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-6">
                  <ArrowUpFromLine className="h-7 w-7 text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-3">
                  Cardless ATM Withdrawals
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Any voucher, wallet, or send-money credential — redeemable at any participating ATM across the network. Bank-format-intelligent routing matches each acquirer's native format automatically.
                </p>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                    <span>Issuer-authoritative authorization (only the issuer decides)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                    <span>Single-use, mTLS-bound transaction tokens (120s TTL)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                    <span>Dynamic acquirer routing with automatic fail-over</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                    <span>Permanent anti-replay voucher ledger</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Deposit card — NEW */}
            <Card className="bg-slate-900/60 border-green-500/20 hover:border-green-500/50 transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 border border-green-500/40 text-xs text-green-300 font-medium">
                  <Zap className="h-3 w-3" />
                  NEW
                </span>
              </div>
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-green-500/10 flex items-center justify-center mb-6">
                  <ArrowDownToLine className="h-7 w-7 text-green-400" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-3">
                  Real-Time Cross-Bank ATM Deposits
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Deposit cash at any bank's ATM and have it credited to any other bank's account in real time — no waiting for overnight batch clearing. Just like Absa, FNB or Standard Bank deposits, but across every participating bank.
                </p>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0 mt-0.5" />
                    <span>Real-time destination-account validation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0 mt-0.5" />
                    <span>Live denomination-audit trail (R10 – R200 notes)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0 mt-0.5" />
                    <span>Configurable FICA/FIC limits — no code changes required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0 mt-0.5" />
                    <span>SMS receipt to depositor's cellphone</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0 mt-0.5" />
                    <span>Automatic settlement ledger — acquirer / issuer / platform</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Deposit flow steps */}
          <div className="mt-16">
            <h3 className="text-center font-heading font-semibold text-xl text-white mb-8">
              How a Cross-Bank Deposit Works
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { n: 1, title: "Enter destination", desc: "Depositor enters receiving bank, account number and their name + cellphone" },
                { n: 2, title: "Insert cash", desc: "ATM counts notes and displays total with full denomination breakdown" },
                { n: 3, title: "Confirm on-screen", desc: "Depositor confirms — 360Wallet clears the credit to the receiving bank in real time" },
                { n: 4, title: "SMS receipt", desc: "Depositor receives instant SMS receipt. Funds available immediately at destination" },
              ].map((s) => (
                <div key={s.n} className="p-6 rounded-xl bg-slate-800/40 border border-slate-700/50">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 border border-green-500/40 font-bold text-green-300 flex items-center justify-center mb-4">
                    {s.n}
                  </div>
                  <h4 className="font-medium text-white mb-2">{s.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Button
                size="lg"
                variant="outline"
                className="border-green-500/30 text-green-300 hover:bg-green-500/10"
                onClick={() => setIsPartnerModalOpen(true)}
              >
                Request a Live Demo for Your Bank
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section id="partners" className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Who It's For
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Connecting key players in the financial ecosystem
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Banks */}
            <Card className="bg-slate-900/50 border-slate-800 hover:border-blue-500/50 transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-6 group-hover:bg-blue-500/20 transition-colors">
                  <Building2 className="h-7 w-7 text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-3">
                  Banks & Financial Institutions
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  Extend withdrawal AND deposit access across every participating network — without deploying additional ATMs or branches.
                </p>
              </CardContent>
            </Card>
            
            {/* ATM Operators */}
            <Card className="bg-slate-900/50 border-slate-800 hover:border-purple-500/50 transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-6 group-hover:bg-purple-500/20 transition-colors">
                  <Network className="h-7 w-7 text-purple-400" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-3">
                  ATM & Switch Operators
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  Double your ATM utilization — every terminal becomes a bi-directional cash-in / cash-out point for all participating banks.
                </p>
              </CardContent>
            </Card>
            
            {/* Retail Partners */}
            <Card className="bg-slate-900/50 border-slate-800 hover:border-green-500/50 transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-green-500/10 flex items-center justify-center mb-6 group-hover:bg-green-500/20 transition-colors">
                  <Store className="h-7 w-7 text-green-400" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-3">
                  Retail Cash-Out Partners
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  Support secure digital cash withdrawals and earn from transaction flows.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Platform Benefits Section */}
      <section id="benefits" className="py-24 bg-slate-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Platform Benefits
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Enterprise-grade infrastructure for modern financial services
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">Secure Authorization</h4>
                <p className="text-sm text-gray-400">End-to-end encrypted transaction authorization</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                <Globe className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">Network Interoperability</h4>
                <p className="text-sm text-gray-400">Seamless connection across financial networks</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <Zap className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">Real-time Validation</h4>
                <p className="text-sm text-gray-400">Instant transaction validation and processing</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">Automated Settlement</h4>
                <p className="text-sm text-gray-400">Streamlined reconciliation and billing</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <Lock className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">Enterprise Security</h4>
                <p className="text-sm text-gray-400">Bank-grade security controls and compliance</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">Regulatory Compliance</h4>
                <p className="text-sm text-gray-400">Built for South African financial regulations</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/50 border border-green-500/30">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <ArrowDownToLine className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">Real-Time Cross-Bank Deposits</h4>
                <p className="text-sm text-gray-400">Cash cleared to any bank instantly — no overnight batching</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                <Banknote className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">Configurable Limits</h4>
                <p className="text-sm text-gray-400">FICA/FIC limits tunable per bank, account or tier — no redeploys</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
            About 360Wallet
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            360Wallet is a financial infrastructure solution developed by <span className="text-white font-medium">Motswagae Holding (Pty) Ltd</span>, focused on enabling interoperable cash access across financial ecosystems in South Africa.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => setIsPartnerModalOpen(true)}
            >
              Become a Partner
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-slate-950">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
                Get in Touch
              </h2>
              <p className="text-gray-400 mb-8">
                Ready to expand your cash access network? Contact us to discuss partnership opportunities.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Email</p>
                    <p className="text-white">info@360wallet.co.za</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Phone</p>
                    <p className="text-white">+27 707358450</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Location</p>
                    <p className="text-white">Johannesburg, South Africa</p>
                  </div>
                </div>
              </div>
            </div>
            
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-8">
                <h3 className="font-heading font-semibold text-xl text-white mb-6">Send us a message</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Name</Label>
                      <Input 
                        placeholder="Your name" 
                        className="bg-slate-800 border-slate-700 text-white"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Company</Label>
                      <Input 
                        placeholder="Company name" 
                        className="bg-slate-800 border-slate-700 text-white"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Email</Label>
                    <Input 
                      type="email" 
                      placeholder="your@email.com" 
                      className="bg-slate-800 border-slate-700 text-white"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Message</Label>
                    <Textarea 
                      placeholder="How can we help?" 
                      className="bg-slate-800 border-slate-700 text-white min-h-[100px]"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                    Send Message
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Wallet className="h-4 w-4 text-white" />
              </div>
              <span className="font-heading font-bold text-white">360Wallet</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <a href="#about" className="text-gray-400 hover:text-white transition-colors">About</a>
              <button onClick={() => setIsPartnerModalOpen(true)} className="text-gray-400 hover:text-white transition-colors">Partnerships</button>
              <button onClick={() => setIsContactModalOpen(true)} className="text-gray-400 hover:text-white transition-colors">Contact</button>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a>
            </div>
            
            <p className="text-sm text-gray-500">
              © 2026 Motswagae Holding (Pty) Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Partnership Modal */}
      <Dialog open={isPartnerModalOpen} onOpenChange={setIsPartnerModalOpen}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Request Partnership</DialogTitle>
            <DialogDescription className="text-gray-400">
              Tell us about your organization and how we can work together.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Name</Label>
                <Input 
                  placeholder="Your name" 
                  className="bg-slate-800 border-slate-700 text-white"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Company</Label>
                <Input 
                  placeholder="Company" 
                  className="bg-slate-800 border-slate-700 text-white"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Email</Label>
              <Input 
                type="email" 
                placeholder="your@email.com" 
                className="bg-slate-800 border-slate-700 text-white"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Organization Type</Label>
              <select 
                className="w-full h-10 rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
              >
                <option value="">Select type...</option>
                <option value="bank">Bank / Financial Institution</option>
                <option value="atm">ATM / Switch Operator</option>
                <option value="retail">Retail Cash-Out Partner</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Message</Label>
              <Textarea 
                placeholder="Tell us about your partnership interest..." 
                className="bg-slate-800 border-slate-700 text-white"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsPartnerModalOpen(false)} className="border-slate-700 text-gray-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Modal */}
      <Dialog open={isContactModalOpen} onOpenChange={setIsContactModalOpen}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Contact Us</DialogTitle>
            <DialogDescription className="text-gray-400">
              Send us a message and we'll get back to you shortly.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Name</Label>
                <Input 
                  placeholder="Your name" 
                  className="bg-slate-800 border-slate-700 text-white"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Phone</Label>
                <Input 
                  placeholder="+27..." 
                  className="bg-slate-800 border-slate-700 text-white"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Email</Label>
              <Input 
                type="email" 
                placeholder="your@email.com" 
                className="bg-slate-800 border-slate-700 text-white"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Message</Label>
              <Textarea 
                placeholder="How can we help?" 
                className="bg-slate-800 border-slate-700 text-white"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsContactModalOpen(false)} className="border-slate-700 text-gray-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Send Message
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
